import { useMemo } from 'react';

import { PartialRecord } from '@tsl-frontend/tsl-utilities';

import {
    BADGE_COPY,
    BadgeCopyKeys,
    claimedBadgeDialogImageMapping,
    getBadgeImages,
} from '../utils';
import { BadgeStatus, BadgeType, IBadge } from '../types';

import { BadgeLocked, DialogBadgeLocked } from '@assets/dashboard';

const getDefaultStatus = (claimed: boolean) => {
    return claimed ? BadgeStatus.BADGE_CLAIMED : BadgeStatus.BADGE_AVAILABLE;
};

const useBadges = (badge: IBadge) => {
    const { code, locked, claimed, badge_type } = badge;

    const badgeStatus: BadgeStatus = useMemo(() => {
        switch (badge_type) {
            case BadgeType.DEFAULT:
                if (locked) return BadgeStatus.BADGE_LOCKED;
                return getDefaultStatus(claimed);
            default:
                return BadgeStatus.BADGE_LOCKED;
        }
    }, [locked, claimed, badge_type]);

    const title = BADGE_COPY[code][BadgeCopyKeys.TITLE] as string;
    const description = (
        BADGE_COPY[code][BadgeCopyKeys.DESCRIPTION] as PartialRecord<
            BadgeStatus,
            string
        >
    )[badgeStatus];

    const buttonData = useMemo(() => {
        return {
            buttonAction: null,
            buttonText: locked ? 'Badge Locked' : 'Claim Badge',
            lockedButton: locked,
        };
    }, [locked]);

    const imgMapping = useMemo(() => getBadgeImages(code), [code]);

    const BadgeComponent = imgMapping.badge[badgeStatus] ?? BadgeLocked;
    const DialogBadgeComponent =
        imgMapping.dialog[badgeStatus] ?? DialogBadgeLocked;
    const dialogLottieAnimation = claimedBadgeDialogImageMapping[code];

    return {
        title,
        description,
        badgeStatus,
        BadgeComponent,
        DialogBadgeComponent,
        dialogLottieAnimation,
        buttonData,
    };
};

export default useBadges;
